import { camelCase, capitalize, sortBy, toLower, uniq } from 'lodash';
import moment from 'moment';

export default function getScheduledInfo({
  startOn,
  amPmGenerated,
  frequency,
  monthlyMode,
  monthlyEvery,
  monthlyEachDates,
  monthlyOnTheDayOption,
  monthlyOnTheFirstLastOption,
  reportingDateDays,
  reportingDateBeforeAfter,
  timeGenerated,
  weeklyEvery,
  weeklyOn,
  reportingDates = '',
  timeZone = '+00:00', // default to UTC
}) {
  let humanizedString = '';

  // all moment suffix is timezone offsetted
  const hour = Number(moment(`${timeGenerated} ${amPmGenerated}`, 'h A').format('H'));
  const startMoment = moment(startOn).utcOffset(timeZone).set('hour', hour).set('minute', 0).set('second', 0);
  const currentMoment = moment.utc().utcOffset(timeZone);
  const currentHour = Number(currentMoment.format('H'));
  const hourOffset = currentHour - hour;
  let nextMoment = moment(startMoment);

  switch (camelCase(frequency)) {
    case 'daily':
      switch (true) {
        case startMoment.isSameOrBefore(currentMoment, 'day'):
          nextMoment = moment(currentMoment).set('hour', hour).set('minute', 0).set('second', 0);

          if (hourOffset >= 0) {
            nextMoment = moment(nextMoment).add(1, 'day');
          }

          break;
        case startMoment.isAfter(currentMoment, 'day'):
          nextMoment = moment(startMoment);
          break;
        default:
          break;
      }

      break;
    case 'weekly':
      let sortedWeeklyOn = sortBy(weeklyOn, Number);
      const defaultDay = sortedWeeklyOn.shift();

      nextMoment.day(defaultDay);

      while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
        if (!sortedWeeklyOn.length) {
          sortedWeeklyOn = sortBy(weeklyOn, Number);

          nextMoment.add(weeklyEvery, 'week');
        }

        const shiftDay = sortedWeeklyOn.shift();

        nextMoment.day(shiftDay);
      }

      humanizedString = `every ${weeklyEvery} week${weeklyEvery > 1 ? 's' : ''} on ${Array.from(uniq(weeklyOn).sort())
        .sort()
        .map((weekday) => moment(weekday, 'd').format('dddd'))
        .join(', ')}`;

      break;
    case 'monthly':
      switch (camelCase(monthlyMode)) {
        case 'each':
          let sortedDates = sortBy(monthlyEachDates, Number);
          const defaultDate = sortedDates.shift();

          nextMoment.set('date', defaultDate);

          while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
            if (!sortedDates.length) {
              sortedDates = sortBy(monthlyEachDates, Number);

              nextMoment.add(monthlyEvery, 'month');
            }

            const shiftDate = sortedDates.shift();

            nextMoment.set('date', shiftDate);
          }

          humanizedString = `every ${monthlyEvery} month${monthlyEvery > 1 ? 's' : ''} on ${Array.from(monthlyEachDates)
            .sort()
            .map((monthDay) => moment().date(monthDay).format('Do'))
            .join(', ')} day`;

          break;
        case 'onThe':
          switch (monthlyOnTheFirstLastOption.toLowerCase()) {
            case 'first':
              if (monthlyOnTheDayOption === 'DAY') {
                nextMoment = nextMoment.startOf('month').set('hour', hour).set('minute', 0).set('second', 0);

                while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
                  nextMoment.add(monthlyEvery, 'month');
                }
              } else {
                const startOfMonth = nextMoment.startOf('month').set('hour', hour).set('minute', 0).set('second', 0);
                nextMoment = moment(startOfMonth).day(capitalize(monthlyOnTheDayOption));

                if (nextMoment.isBefore(startOfMonth)) {
                  nextMoment.add(1, 'week');
                }

                while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
                  nextMoment.add(monthlyEvery, 'month');

                  const nextStartOfMonth = nextMoment.startOf('month').set('hour', hour).set('minute', 0).set('second', 0);
                  nextMoment = moment(nextStartOfMonth).day(capitalize(monthlyOnTheDayOption));

                  if (nextMoment.isBefore(nextStartOfMonth)) {
                    nextMoment.add(1, 'week');
                  }
                }
              }

              break;
            case 'last':
              if (monthlyOnTheDayOption === 'DAY') {
                nextMoment = nextMoment.endOf('month').set('hour', hour).set('minute', 0).set('second', 0);

                while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
                  nextMoment.add(monthlyEvery, 'month').endOf('month').set('hour', hour).set('minute', 0).set('second', 0);
                }
              } else {
                const endOfMonth = nextMoment.endOf('month').set('hour', hour).set('minute', 0).set('second', 0);
                nextMoment = moment(endOfMonth).day(capitalize(monthlyOnTheDayOption));

                if (nextMoment.isAfter(endOfMonth)) {
                  nextMoment.subtract(1, 'week');
                }

                while (nextMoment.isBefore(currentMoment) || nextMoment.isBefore(startMoment)) {
                  nextMoment.add(monthlyEvery, 'month');

                  const nextEndOfMonth = nextMoment.endOf('month').set('hour', hour).set('minute', 0).set('second', 0);
                  nextMoment = moment(nextEndOfMonth).day(capitalize(monthlyOnTheDayOption));

                  if (nextMoment.isAfter(nextEndOfMonth)) {
                    nextMoment.subtract(1, 'week');
                  }
                }
              }
              break;
            default:
              break;
          }

          humanizedString = `every ${monthlyEvery} month${monthlyEvery > 1 ? 's' : ''} on the ${toLower(
            monthlyOnTheFirstLastOption,
          )} ${capitalize(monthlyOnTheDayOption)}`;
          break;
        default:
          break;
      }

      break;
    case 'reportingDate':
      const reportDates = (reportingDates || '').split(', ').sort((a, b) => new Date(a) - new Date(b));
      let expectMoment;

      switch (reportingDateBeforeAfter.toLowerCase()) {
        case 'before':
          expectMoment = currentMoment.add(reportingDateDays, 'day');

          break;
        case 'after':
          expectMoment = currentMoment.subtract(reportingDateDays, 'day');

          break;
        default:
          break;
      }

      const nextDate = reportDates.find((date) => moment.utc(date).isAfter(expectMoment) && moment.utc(date).isAfter(currentMoment));
      nextMoment = moment.utc(nextDate).set('hour', 0).set('minute', 0).set('second', 0);

      break;
    default:
      break;
  }

  return { nextMoment, humanizedString };
}
